import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Badge, Button, Card, Input, Stat, Switch, Tabs, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { getLeadInsights, getLeads, qk, setLeadStatus, setLeadValue, addManualLead, importLeads, type ManualLeadInput, type LeadInsights as LeadInsightsData } from '@/data/client';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { useSearch } from '@/components/shell/search';
import {
  ChannelBadge, LeadDrawer, ScoreDots, StatusSelect,
  channelMeta, fmtMoney, fmtWhen, normSource, sourceMeta,
} from '@/components/LeadDrawer';
import { LEAD_STATUSES, type LeadStatus } from '@/data/status';
import type { Channel, Lead } from '@/data/types';

function MiniDash({
  leads, threshold, setThreshold, showSplit,
}: {
  leads: Lead[]; threshold: number; setThreshold: (n: number) => void; showSplit: boolean;
}) {
  const total = leads.length;
  const highQ = leads.filter((l) => l.score >= threshold).length;
  const paid = leads.filter((l) => sourceMeta(l.source).paid).length;
  const organic = total - paid;
  const pct = total ? Math.round((highQ / total) * 100) : 0;
  return (
    <div className={`lead-mini ${showSplit ? 'lead-mini--4' : 'lead-mini--2'}`}>
      <Card padding="lg" className="lead-mini__card">
        <Stat label="Captured Leads" value={total} sub="in selected range" info="Real leads Triton captured and AI-graded — forms, tracked calls, and chats. The source of truth for actual leads; it won't match GA4 or Google Ads conversions (those count differently and can miss untracked phone calls)." />
      </Card>
      <Card padding="lg" className="lead-mini__card">
        <div className="lead-mini__qhead">
          <Stat label={`Quality ≥ ${threshold}`} value={highQ} sub={`${pct}% of total`} />
          <div className="lead-mini__stepper" role="group" aria-label="AI score threshold">
            <button onClick={() => setThreshold(Math.max(1, threshold - 1))} disabled={threshold <= 1} aria-label="Lower threshold">–</button>
            <span title="Minimum AI quality score">{threshold}+</span>
            <button onClick={() => setThreshold(Math.min(5, threshold + 1))} disabled={threshold >= 5} aria-label="Raise threshold">+</button>
          </div>
        </div>
      </Card>
      {showSplit && (
        <>
          <Card padding="lg" className="lead-mini__card">
            <Stat label="Organic leads" value={organic} sub="UTM: non-paid" />
            <span className="lead-mini__tag lead-mini__tag--organic"><Icon name="sprout" size={13} /> Organic</span>
          </Card>
          <Card padding="lg" className="lead-mini__card">
            <Stat label="Paid ad leads" value={paid} sub="UTM: paid" />
            <span className="lead-mini__tag lead-mini__tag--paid"><Icon name="megaphone" size={13} /> Paid</span>
          </Card>
        </>
      )}
    </div>
  );
}


const CH_LABEL: Record<string, string> = { email: 'Email', chat: 'Chat', phone: 'Phone', sms: 'SMS', other: 'Other' };
function fmtAgo(iso?: string): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

/* AI analysis of the leads in the selected date range: average quality, top
   sources and channel mix (computed server-side), plus a cached Claude read of
   the call/chat/email content — what leads are actually asking for. */
function LeadInsights({ range }: { range: string }) {
  const qc = useQueryClient();
  const key = [...qk.leadInsights, range];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getLeadInsights(range), staleTime: 5 * 60_000 });
  const refresh = useMutation({
    mutationFn: () => getLeadInsights(range, true),
    onSuccess: (d) => qc.setQueryData(key, d),
  });
  const d = data as LeadInsightsData | undefined;

  if (isLoading) return <Card padding="lg" className="li-panel"><p className="dash-muted">Analyzing leads…</p></Card>;
  if (!d || !d.configured || d.empty) return null;

  const rangeLabel = range === 'all' ? 'all time' : `the last ${range} days`;
  const asking = d.askingFor;
  return (
    <Card padding="lg" className="li-panel">
      <div className="li-head">
        <div className="li-title">
          <span className="li-mark"><Icon name="sparkles" size={16} /></span>
          <div>
            <h2>AI lead insights</h2>
            <p className="dash-muted">Based on {d.count} lead{d.count === 1 ? '' : 's'} from {rangeLabel}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="refresh-cw" size={14} />} disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <div className="li-grid">
        <div className="li-metrics">
          <div className="li-metric">
            <span className="li-metric__label">Avg quality</span>
            <span className="li-metric__val">{d.avgScore != null ? d.avgScore.toFixed(1) : '—'}<em>/5</em></span>
            {d.avgScore != null && (
              <span className="li-dots" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((i) => <i key={i} className={i <= Math.round(d.avgScore!) ? 'is-on' : ''} />)}
              </span>
            )}
          </div>
          <div className="li-metric">
            <span className="li-metric__label">Top sources</span>
            <ul className="li-list">
              {(d.topSources ?? []).length === 0
                ? <li className="dash-muted">—</li>
                : (d.topSources ?? []).map((s) => (
                  <li key={s.source}><span title={s.avgScore != null ? `${s.avgScore.toFixed(1)}★ avg · ${s.sold} sold` : `${s.sold} sold`}>{s.source}</span><b>{s.count}</b></li>
                ))}
            </ul>
          </div>
          <div className="li-metric">
            <span className="li-metric__label">Channels</span>
            <ul className="li-list">
              {(d.channels ?? []).map((c) => (
                <li key={c.channel}><span>{CH_LABEL[c.channel] ?? c.channel}</span><b>{c.count}</b></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="li-asking">
          <h3 className="li-asking__title"><Icon name="quote" size={13} /> What leads are asking for</h3>
          {d.aiError
            ? <p className="dash-muted">{d.aiError.includes('ANTHROPIC') ? 'The AI key isn’t configured on the Worker.' : 'Couldn’t analyze right now — try Refresh.'}</p>
            : asking && (asking.summary || asking.themes.length > 0) ? (
              <>
                {asking.summary && <p className="li-asking__sum">{asking.summary}</p>}
                {asking.themes.length > 0 && (
                  <div className="li-themes">
                    {asking.themes.map((t, i) => (
                      <span key={i} className="li-theme">{t.label}{t.share ? <em>{t.share}%</em> : null}</span>
                    ))}
                  </div>
                )}
                {d.aiGeneratedAt && <p className="li-stamp">Analysis from {fmtAgo(d.aiGeneratedAt)} · updates daily · Refresh for the latest</p>}
              </>
            ) : <p className="dash-muted">Not enough lead content to summarize yet.</p>}
          {d.tip && (
            <div className="li-tip">
              <span className="li-tip__icon"><Icon name="alert-circle" size={14} /></span>
              <p><strong>Tip</strong> · {d.tip}</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

const CHANNEL_TABS = [
  { value: 'all', label: 'All Leads' },
  { value: 'email', label: 'Email' },
  { value: 'chat', label: 'Chat' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'sms', label: 'SMS' },
];
const DATE_OPTS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];
const SOURCE_OPTS = [
  { value: 'all', label: 'All sources' },
  { value: 'organic', label: 'Organic' },
  { value: 'ai', label: 'AI' },
  { value: 'paid', label: 'Paid' },
];
const STATUS_FILTER_OPTS = [
  { value: 'all', label: 'All statuses' },
  ...LEAD_STATUSES.map((s) => ({ value: s.value, label: s.label })),
];

type Override = { status?: LeadStatus; value?: number | null };

export function LeadIQ() {
  const { data: rawLeads = [], isLoading, isError, error } = useQuery({ queryKey: qk.leads, queryFn: getLeads });
  const { query } = useSearch();

  const [channel, setChannel] = useState('all');
  const [range, setRange] = useState('30');
  const [source, setSource] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [threshold, setThreshold] = useState(3);
  const [overrides, setOverrides] = useState<Record<string, Override>>({});
  const [activeId, setActiveId] = useState<string | null>(null);

  const isAll = channel === 'all';

  const leads = useMemo(
    () => rawLeads.map((l) => ({ ...l, ...overrides[l.uid] })),
    [rawLeads, overrides],
  );

  const setStatus = (uid: string, status: LeadStatus) => {
    setOverrides((o) => ({ ...o, [uid]: { ...o[uid], status } }));
    void setLeadStatus(uid, status);
  };
  const saveValue = (uid: string, value: number | null) => {
    setOverrides((o) => ({ ...o, [uid]: { ...o[uid], value } }));
    void setLeadValue(uid, value);
  };

  const inRange = (l: Lead) => range === 'all' || l.days <= Number(range);

  const q = query.trim().toLowerCase();
  const matchesSearch = (l: Lead) =>
    !q || [l.name, l.email, l.phone, l.company, l.city].some((v) => v?.toLowerCase().includes(q));

  const filtered = leads
    .filter((l) => isAll || l.channel === channel)
    .filter(inRange)
    .filter((l) => source === 'all' || normSource(l.source) === source)
    .filter((l) => statusFilter === 'all' || l.status === statusFilter)
    .filter(matchesSearch)
    .sort((a, b) => a.days - b.days);

  const tabs = CHANNEL_TABS.map((t) => ({
    ...t,
    count: t.value === 'all' ? leads.filter(inRange).length : leads.filter((l) => l.channel === t.value && inRange(l)).length,
  }));

  const active = activeId != null ? leads.find((l) => l.uid === activeId) ?? null : null;

  return (
    <div className="dash-stack">
      <div className="lead-tabsbar">
        <Tabs items={tabs} value={channel} onChange={setChannel} variant="pill" />
      </div>

      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">
          <label className="lead-field">
            <Icon name="calendar" size={15} />
            <select className="tr-select" value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range">
              {DATE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="lead-field">
            <Icon name="filter" size={15} />
            <select className="tr-select" value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
              {SOURCE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="lead-field">
            <Icon name="check-circle" size={15} />
            <select className="tr-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
              {STATUS_FILTER_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <LeadAdminTools />
      </div>

      <MiniDash leads={filtered} threshold={threshold} setThreshold={setThreshold} showSplit={isAll} />

      <LeadInsights range={range} />

      <Card padding={false} className="dash-leads">
        <div className="dash-panelhead dash-panelhead--pad">
          <div>
            <h2>{isAll ? 'All leads' : `${channelMeta(channel as Channel).label} leads`}</h2>
            <p>{filtered.length} lead{filtered.length !== 1 ? 's' : ''} · sorted by most recent</p>
          </div>
        </div>
        {isLoading ? (
          <div className="lead-none">Loading leads…</div>
        ) : isError ? (
          <div className="lead-none">Couldn’t load leads: {error instanceof Error ? error.message : 'error'}</div>
        ) : filtered.length === 0 ? (
          <div className="lead-none">No leads match these filters.</div>
        ) : (
          <table className="dash-table lead-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>AI quality</th>
                {isAll && <th>Channel</th>}
                <th>Source</th>
                {isAll && <th>Value</th>}
                <th>Status</th>
                <th>Received</th>
                <th className="lead-th-actions">Read</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.uid} className={l.status === 'unworked' ? 'lead-row--unworked' : ''} onClick={() => setActiveId(l.uid)}>
                  <td><div className="dash-cust"><Avatar name={l.name} size="sm" tone="cream" /><span>{l.name}</span></div></td>
                  <td><ScoreDots score={l.score} /></td>
                  {isAll && <td><ChannelBadge channel={l.channel} /></td>}
                  <td><Badge variant={sourceMeta(normSource(l.source)).variant}>{sourceMeta(normSource(l.source)).label}</Badge></td>
                  {isAll && <td className="dash-val">{fmtMoney(l.value)}</td>}
                  <td onClick={(e) => e.stopPropagation()}>
                    <StatusSelect value={l.status} onChange={(s) => setStatus(l.uid, s)} />
                  </td>
                  <td className="dash-time">{fmtWhen(l.days)}</td>
                  <td className="lead-td-actions" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => setActiveId(l.uid)} iconLeft={<Icon name="sparkles" size={14} />}>Read</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <LeadDrawer lead={active} onClose={() => setActiveId(null)} onSetStatus={setStatus} onSaveValue={saveValue} />
    </div>
  );
}

/* ---- manual add + CSV import (admin only) ---------------------------- */

const SOURCE_OPTS_IN = [
  { value: 'organic', label: 'Organic' }, { value: 'paid', label: 'Paid' },
  { value: 'referral', label: 'Referral' }, { value: 'ai', label: 'AI' },
];
const CHANNEL_OPTS_IN = [
  { value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' }, { value: 'chat', label: 'Chat' },
];

// D1 fields we can map a CSV column onto, with header-name auto-detect patterns.
const IMPORT_FIELDS: { key: keyof ManualLeadInput; label: string; re: RegExp }[] = [
  { key: 'email', label: 'Email', re: /e-?mail/i },
  { key: 'phone', label: 'Phone', re: /phone|tel|mobile|cell/i },
  { key: 'full_name', label: 'Full name', re: /^(full\s*)?name$|your\s*name|contact\s*name/i },
  { key: 'first_name', label: 'First name', re: /first|fname|given/i },
  { key: 'last_name', label: 'Last name', re: /last|lname|surname|family/i },
  { key: 'company', label: 'Company', re: /company|business|organi/i },
  { key: 'address', label: 'Address', re: /address|street/i },
  { key: 'city', label: 'City', re: /city|town/i },
  { key: 'state', label: 'State', re: /state|province|region/i },
  { key: 'zip', label: 'Zip', re: /zip|postal|post\s*code/i },
  { key: 'receivedAt', label: 'Date received', re: /date|created|time|submitted/i },
];

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const s = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let q = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) {
      if (ch === '"') { if (s[i + 1] === '"') { field += '"'; i++; } else q = false; }
      else field += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else field += ch;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  const nonEmpty = rows.filter((r) => r.some((c) => c.trim() !== ''));
  const headers = (nonEmpty.shift() || []).map((h) => h.trim());
  return { headers, rows: nonEmpty };
}

function autoMap(headers: string[]): Record<string, string> {
  const m: Record<string, string> = {};
  const used = new Set<string>();
  for (const def of IMPORT_FIELDS) {
    const h = headers.find((x) => !used.has(x) && def.re.test(x));
    if (h) { m[def.key as string] = h; used.add(h); }
  }
  return m;
}

function LeadAdminTools() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const [modal, setModal] = useState<null | 'add' | 'import'>(null);
  if (!isAdmin) return null;
  const done = () => { qc.invalidateQueries({ queryKey: qk.leads }); setModal(null); };
  return (
    <>
      <div className="lead-toolbar__actions">
        <Button variant="outline" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setModal('add')}>Add lead</Button>
        <Button variant="outline" size="sm" iconLeft={<Icon name="download" size={14} />} onClick={() => setModal('import')}>Import CSV</Button>
      </div>
      {modal === 'add' && <AddLeadModal onClose={() => setModal(null)} onDone={done} />}
      {modal === 'import' && <ImportLeadsModal onClose={() => setModal(null)} onDone={done} />}
    </>
  );
}

function AddLeadModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState<ManualLeadInput>({});
  const [source, setSource] = useState('organic');
  const [channel, setChannel] = useState('email');
  const [grade, setGrade] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof ManualLeadInput, v: string) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.email && !f.phone && !f.full_name && !f.message) { setErr('Enter at least a name, email, phone, or message.'); return; }
    setBusy(true); setErr('');
    try { await addManualLead({ ...f, source, channel, grade }); onDone(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };

  return (
    <div className="lead-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="lead-modal__card" onClick={(e) => e.stopPropagation()}>
        <div className="lead-modal__head"><h3>Add a lead</h3><button className="lead-modal__x" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button></div>
        <div className="lead-modal__body">
          <div className="lead-modal__grid">
            <Input label="Full name" value={f.full_name || ''} onChange={(e) => set('full_name', e.target.value)} />
            <Input label="Email" value={f.email || ''} onChange={(e) => set('email', e.target.value)} />
            <Input label="Phone" value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} />
            <Input label="Company" value={f.company || ''} onChange={(e) => set('company', e.target.value)} />
            <Input label="City" value={f.city || ''} onChange={(e) => set('city', e.target.value)} />
            <Input label="State" value={f.state || ''} onChange={(e) => set('state', e.target.value)} />
            <Input label="Zip" value={f.zip || ''} onChange={(e) => set('zip', e.target.value)} />
            <Input label="Date received" type="date" value={f.receivedAt || ''} onChange={(e) => set('receivedAt', e.target.value)} />
          </div>
          <Textarea label="Message / notes" rows={4} value={f.message || ''} onChange={(e) => set('message', e.target.value)} hint="Used for AI grading." />
          <div className="lead-modal__grid">
            <label className="tr-field"><span className="tr-field__label">Source</span>
              <select className="tr-select" value={source} onChange={(e) => setSource(e.target.value)}>{SOURCE_OPTS_IN.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            </label>
            <label className="tr-field"><span className="tr-field__label">Channel</span>
              <select className="tr-select" value={channel} onChange={(e) => setChannel(e.target.value)}>{CHANNEL_OPTS_IN.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            </label>
          </div>
          <Switch label="AI-grade this lead on save" checked={grade} onChange={(e) => setGrade(e.target.checked)} />
          {err && <p className="err">{err}</p>}
        </div>
        <div className="lead-modal__foot">
          <button className="cq-cancel" onClick={onClose} disabled={busy}>Cancel</button>
          <Button variant="primary" size="sm" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Add lead'}</Button>
        </div>
      </div>
    </div>
  );
}

function ImportLeadsModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [source, setSource] = useState('organic');
  const [channel, setChannel] = useState('email');
  const [grade, setGrade] = useState(true);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<{ inserted: number; graded: number } | null>(null);
  const [err, setErr] = useState('');

  const onFile = async (file: File | null) => {
    if (!file) return;
    setErr(''); setResult(null);
    try {
      const { headers: h, rows: r } = parseCsv(await file.text());
      if (!h.length) { setErr('No header row found in that file.'); return; }
      setHeaders(h); setRows(r); setMapping(autoMap(h)); setFileName(file.name);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not read the file.'); }
  };

  const buildRows = (): ManualLeadInput[] => {
    const idx: Record<string, number> = {};
    headers.forEach((h, i) => { idx[h] = i; });
    const get = (r: string[], field: string) => { const h = mapping[field]; return h && idx[h] != null ? (r[idx[h]] || '').trim() : ''; };
    return rows.map((r) => ({
      email: get(r, 'email'), phone: get(r, 'phone'), full_name: get(r, 'full_name'),
      first_name: get(r, 'first_name'), last_name: get(r, 'last_name'), company: get(r, 'company'),
      address: get(r, 'address'), city: get(r, 'city'), state: get(r, 'state'), zip: get(r, 'zip'),
      receivedAt: get(r, 'receivedAt'),
      message: headers.map((h, i) => ((r[i] || '').trim() ? `${h}: ${(r[i] || '').trim()}` : '')).filter(Boolean).join('\n'),
    }));
  };

  const run = async () => {
    const built = buildRows().filter((x) => x.email || x.phone || x.full_name || x.message);
    if (!built.length) { setErr('No usable rows to import.'); return; }
    setBusy(true); setErr(''); setResult(null);
    const batchSize = grade ? 10 : 100;
    let inserted = 0;
    let graded = 0;
    setProgress({ done: 0, total: built.length });
    try {
      for (let i = 0; i < built.length; i += batchSize) {
        const batch = built.slice(i, i + batchSize);
        const r = await importLeads({ rows: batch, source, channel, grade });
        inserted += r.inserted || 0; graded += r.graded || 0;
        setProgress({ done: Math.min(i + batchSize, built.length), total: built.length });
      }
      setResult({ inserted, graded });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="lead-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="lead-modal__card lead-modal__card--wide" onClick={(e) => e.stopPropagation()}>
        <div className="lead-modal__head"><h3>Import leads from CSV</h3><button className="lead-modal__x" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button></div>
        <div className="lead-modal__body">
          <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
          {headers.length > 0 && (
            <>
              <p className="dash-muted" style={{ marginTop: 10 }}>{fileName} — <strong>{rows.length}</strong> rows. Map the columns (auto-filled; adjust as needed). Unmapped columns still feed the AI grader as context.</p>
              <div className="lead-import__map">
                {IMPORT_FIELDS.map((def) => (
                  <label key={def.key as string} className="tr-field">
                    <span className="tr-field__label">{def.label}</span>
                    <select className="tr-select" value={mapping[def.key as string] || ''} onChange={(e) => setMapping((m) => ({ ...m, [def.key as string]: e.target.value }))}>
                      <option value="">(none)</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <div className="lead-modal__grid">
                <label className="tr-field"><span className="tr-field__label">Source (all rows)</span>
                  <select className="tr-select" value={source} onChange={(e) => setSource(e.target.value)}>{SOURCE_OPTS_IN.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </label>
                <label className="tr-field"><span className="tr-field__label">Channel (all rows)</span>
                  <select className="tr-select" value={channel} onChange={(e) => setChannel(e.target.value)}>{CHANNEL_OPTS_IN.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                </label>
              </div>
              <Switch label="AI-grade each row on import (slower on big files)" checked={grade} onChange={(e) => setGrade(e.target.checked)} />
            </>
          )}
          {progress && !result && <p className="dash-muted">Importing… {progress.done}/{progress.total}</p>}
          {result && <p className="reco__msg">Imported {result.inserted} lead{result.inserted === 1 ? '' : 's'}{result.graded ? `, graded ${result.graded}` : ''}. If they carry historical dates, set the date range to <strong>All time</strong> to see them.</p>}
          {err && <p className="err">{err}</p>}
        </div>
        <div className="lead-modal__foot">
          <button className="cq-cancel" onClick={onClose} disabled={busy}>{result ? 'Close' : 'Cancel'}</button>
          {result ? <Button variant="primary" size="sm" onClick={onDone}>Done</Button>
            : <Button variant="primary" size="sm" onClick={run} disabled={busy || headers.length === 0}>{busy ? 'Importing…' : `Import ${rows.length || ''} leads`}</Button>}
        </div>
      </div>
    </div>
  );
}
